<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import { useUI } from '@/composables/useUI'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoSelect from '@/components/base/campos/CampoSelect.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalFormulario from '@/components/base/modal/ModalFormulario.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { CATFIN_TP_MOV } from '@/constants/CATFIN_TP_MOV'
import { ST } from '@/constants/ST'
import { FIN_ST_PLAN_CATEGFIN } from '@/constants/FIN_ST_PLAN_CATEGFIN'
import { PROD_NATUREZA } from '@/constants/PROD_NATUREZA'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'
import { criarConfigBuscaCategoriaFinanceira } from '@/search-definitions/categoriaFinanceiraSearch'
import { criarConfigBuscaNcm } from '@/search-definitions/ncmSearch'
import { criarConfigBuscaCliente } from '@/search-definitions/clienteSearch'
import { criarConfigBuscaTipoProduto } from '@/search-definitions/tipoProdutoSearch'

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

// GUARDIÃO: Modo Xerife (Se COMPLIANCE, exige os campos fiscais)
const isPlanoCompliance = computed(() => authStore.lstNivelModuloFiscal === NV_MODULO_FISCAL.Compliance)

// --- ESTADO DE AUDITORIA ---
const mostrarAuditoria = ref(false)

const ncmSearch = computed(() => criarConfigBuscaNcm())
const confBuscaTipoProduto = computed(() => criarConfigBuscaTipoProduto(authStore.organizacaoAtiva))

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idProduto: 0,
      idOrganizacao: null,
      sku: '',
      nome: '',
      codigoBarras: '',
      codigoReferenciaMercado: '',
      codigoSistemaAntigo: '',
      idTipoProduto: null,
      idNcm: null,
      codigoNcm: '',
      codigoOrigemMercadoria: '',
      descricaoOrigemMercadoria: '',
      codigoGenero: '',
      exIpi: '',
      lstCodigoLc116: '',
      lstCodigoNbs: '',
      reterIssPadrao: false,
      pesoBruto: null,
      pesoLiquido: null,
      cubagem: null,
      lstUnidadeMedidaBase: '',
      lstUnidadeMedidaVolume: '',
      lstStatus: null,
      idCategoriaFinanceiraEntrada: null,
      idCategoriaFinanceiraSaida: null,
      lstTipoTaxaConversaoPadrao: '',
      atributosDinamicos: [],
      valoresAtributosFiscais: [],
      unidadesConversao: [],
      margemEsperadaPercentual: null
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification', 'changesDetected'])

const { obterClasseStatusGhost } = useUI()

const { item, isLoading: isSaving, isEditing, entityInfo, submitForm, handleCancel, initializeForm } = useFormulario(props, emit)

// ============================================================
// ESTADO DE SALVAMENTO (Salvar e Continuar)
// ============================================================
const isProdutoSaved = ref(isEditing.value)
const isSavingProdutoLocal = ref(false)
const isFramesSecundariosBloqueados = computed(() => !isProdutoSaved.value || isSaving.value)

// ============================================================
// FUNÇÃO: Salvar Produto Localmente (criação inicial)
// ============================================================
const salvarProdutoLocalmente = async () => {
  if (isSavingProdutoLocal.value) return

  // Validação básica
  if (!item.value.sku && isSkuManual.value) {
    emit('show-notification', { message: 'O campo SKU é obrigatório.', type: 'warning' })
    return
  }
  if (!item.value.nome) {
    emit('show-notification', { message: 'O campo Descrição Comercial é obrigatório.', type: 'warning' })
    return
  }

  isSavingProdutoLocal.value = true
  const payload = montarPayloadProduto()
  if (!payload) return

  try {
    const response = await api.post('/Produtos', payload)
    item.value = response.data
    isProdutoSaved.value = true
    isEditing.value = true
    entityInfo.value = { id: item.value.idProduto, name: item.value.nome || item.value.sku, nameLabel: 'Produto' }
    emit('show-notification', { message: 'Produto salvo. Agora pode adicionar atributos, unidades e cláusulas.', type: 'success' })
    // Abre os frames secundários
    accordionState.financeiro = true
    accordionState.unidades = true
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar o produto.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingProdutoLocal.value = false
  }
}

// ============================================================
// FUNÇÃO: Salvar Alterações (via @submit-continuar)
// ============================================================
const salvarAlteracoes = async () => {
  if (isSaving.value || !isProdutoSaved.value) return

  const payload = montarPayloadProduto()
  if (!payload) return

  try {
    const res = await api.put(`/Produtos/${item.value.idProduto}`, payload)
    if (res?.data) {
      // Re-inicializa o formulário com os dados atualizados para resetar dirty checking
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar alterações.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

const simNaoOptions = [
  { id: true, label: 'Sim' },
  { id: false, label: 'Não' }
]

// Array fixo solicitado pelo Utilizador
const origensMercadoria = [
  { id: '0', label: '0 - Nacional, exceto as indicadas nos códigos 3, 4, 5 e 8' },
  { id: '1', label: '1 - Estrangeira - Importação direta' },
  { id: '2', label: '2 - Estrangeira - Adquirida no mercado interno' },
  { id: '3', label: '3 - Nacional, mercadoria ou bem com Conteúdo de Importação > 40% e <= 70%' },
  { id: '4', label: '4 - Nacional, cuja produção tenha sido feita em conformidade com PPB' },
  { id: '5', label: '5 - Nacional, mercadoria ou bem com Conteúdo de Importação <= 40%' },
  { id: '6', label: '6 - Estrangeira - Importação direta, sem similar nacional, em lista CAMEX' },
  { id: '7', label: '7 - Estrangeira - Adquirida no mercado interno, sem similar nacional, em lista CAMEX' },
  { id: '8', label: '8 - Nacional, mercadoria ou bem com Conteúdo de Importação > 70%' }
]

const accordionState = reactive({
  basico: true,
  logistica: false,
  fiscal: false,
  financeiro: false,
  unidades: false,
  exigenciasLicenca: false,
  nomesIdiomas: false,
  clausulasComerciais: false
})

const toggleAccordion = (key) => {
  accordionState[key] = !accordionState[key]
}

const exigenciasLicenca = ref([])
const modalExigencia = ref(false)
const exigenciaEmEdicao = ref(null)
const isSavingExigencia = ref(false)
const nomesIdiomas = ref([])
const modalNomeIdioma = ref(false)
const nomeIdiomaEmEdicao = ref(null)
const isSavingNomeIdioma = ref(false)
const clausulasComerciais = ref([])
const modalClausula = ref(false)
const clausulaEmEdicao = ref(null)
const isSavingClausula = ref(false)

// --- Toggle Cláusulas Gerais / Por Cliente ---
const toggleClausula = ref('gerais')
const clausulasPorCliente = ref([])
const modalClausulaCliente = ref(false)
const clausulaClienteEmEdicao = ref(null)
const isSavingClausulaCliente = ref(false)

const listas = reactive({})

const tipoProdutoSelecionado = ref(null)
const tiposProdutoCache = ref([])

// GUARDIÃO: Variável de Natureza (Reativa) - usa constante PROD_NATUREZA
const naturezaSelecionada = computed(() => {
  return tipoProdutoSelecionado.value?.lstNaturezaItem || PROD_NATUREZA.ProdutoFisico
})

const isSkuManual = computed(() => {
  if (!tipoProdutoSelecionado.value) return true
  return tipoProdutoSelecionado.value.formatoGeracaoSku === 'MANUAL'
})

// GUARDIÃO: Campos financeiros obrigatórios conforme flags do TipoProduto
const isComercializavel = computed(() => tipoProdutoSelecionado.value?.comercializavel === true)
const isCompravel = computed(() => tipoProdutoSelecionado.value?.compravel === true)

// GUARDIÃO: Gestão de Estoque por Unidade Logística (Catch Weight)
const isVolumeEnabled = computed(() => tipoProdutoSelecionado.value?.gerirEstoquePorUnidadeLogistica === true)

// --- Plano de Categoria Financeira vigente ---
const idPlanoCategoriaFinanceira = ref(null)

/** Resolve o plano de categoria financeira ativo e vigente para a data atual */
const resolverPlanoCategoria = async () => {
  try {
    const res = await api.get('/PlanosCategoriasFinanceiras', {
      params: {
        lstStatusPlano: FIN_ST_PLAN_CATEGFIN.Ativo,
        idEmpresa: authStore.empresaAtiva,
        pageSize: 50
      }
    })
    const hoje = new Date()
    const plano = (res.data || []).find(p => {
      if (!p.dataInicioVigencia || !p.dataFimVigencia) return false
      const inicio = new Date(p.dataInicioVigencia + 'T00:00:00')
      const fim = new Date(p.dataFimVigencia + 'T00:00:00')
      return hoje >= inicio && hoje <= fim
    })
    idPlanoCategoriaFinanceira.value = plano ? plano.idPlanoCategoriaFinanceira : null
    if (!idPlanoCategoriaFinanceira.value) {
      console.warn('[ProdutosCadastro] Nenhum plano ativo vigente encontrado para a data atual.')
    }
  } catch (e) {
    console.warn('[ProdutosCadastro] Erro ao resolver plano vigente:', e)
    idPlanoCategoriaFinanceira.value = null
  }
}

// --- Lógica: Integração Financeira (Campo Busca Genérico) ---
// Usa a factory centralizada que gerencia filtrosFixos, idPlanoCategoriaFinanceira e permiteLancamento
const confBuscaCatEntrada = computed(() =>
  criarConfigBuscaCategoriaFinanceira(
    'Pesquisar Categoria Financeira (Entrada)',
    CATFIN_TP_MOV.Receita,
    ST.Ativo,
    authStore.organizacaoAtiva,
    null,
    idPlanoCategoriaFinanceira.value,
    true
  )
)

const confBuscaCatSaida = computed(() =>
  criarConfigBuscaCategoriaFinanceira(
    'Pesquisar Categoria Financeira (Saída)',
    `${CATFIN_TP_MOV.Custo},${CATFIN_TP_MOV.Despesa},${CATFIN_TP_MOV.Imposto}`,
    ST.Ativo,
    authStore.organizacaoAtiva,
    null,
    idPlanoCategoriaFinanceira.value,
    true
  )
)

const atributosComerciaisSku = ref([])
const atributosComerciaisGerais = ref([])
const respostasComerciais = reactive({})

watch(() => item.value.idTipoProduto, async (newTipoId) => {
  if (!newTipoId) {
    atributosComerciaisSku.value = []
    atributosComerciaisGerais.value = []
    tipoProdutoSelecionado.value = null
    return
  }

  try {
    const resTipo = await api.get(`/TiposProduto/${newTipoId}`)
    tipoProdutoSelecionado.value = resTipo.data

    if (!isEditing.value && !isSkuManual.value) {
      item.value.sku = ''
    }

    // HERANÇA: Integração Financeira
    if (resTipo.data.idCategoriaFinanceiraEntrada && !item.value.idCategoriaFinanceiraEntrada) {
      item.value.idCategoriaFinanceiraEntrada = resTipo.data.idCategoriaFinanceiraEntrada
    }
    if (resTipo.data.idCategoriaFinanceiraSaida && !item.value.idCategoriaFinanceiraSaida) {
      item.value.idCategoriaFinanceiraSaida = resTipo.data.idCategoriaFinanceiraSaida
    }

    const listasExigidas = resTipo.data.listasVinculadas || []
    const configSku = []
    const configGerais = []

    for (const vinculo of listasExigidas) {
      try {
        const prefixo = vinculo.prefixoItemLista
        const resLista = await api.get(`/ListasCliente/por-prefixo/${prefixo}`)
        const resItens = await api.get(`/ListasCliente/lookup/${prefixo}`)

        const configObj = {
          idCabecalho: prefixo,
          nomeLista: resLista.data.nomeLista,
          opcoes: resItens.data
        }

        if (vinculo.compoeCodigoProduto) configSku.push(configObj)
        else configGerais.push(configObj)

        if (respostasComerciais[prefixo] === undefined) {
          respostasComerciais[prefixo] = ''
        }
      } catch (e) {
        console.warn(`Lista auxiliar ${vinculo.prefixoItemLista} não encontrada.`)
      }
    }

    atributosComerciaisSku.value = configSku
    atributosComerciaisGerais.value = configGerais
  } catch (error) {
    console.error('Erro ao carregar o tipo de produto:', error)
  }
}, { immediate: true })

watch(respostasComerciais, () => {
  if (isEditing.value) return

  if (tipoProdutoSelecionado.value?.formatoGeracaoSku === 'ATRIBUTOS') {
    const pedacos = []
    atributosComerciaisSku.value.forEach(attr => {
      const val = respostasComerciais[attr.idCabecalho]
      if (val) {
        const objLista = attr.opcoes.find(o => String(o.idListaClienteItem) === String(val) || String(o.idListaSistemaItem) === String(val))
        pedacos.push(objLista ? (objLista.textoLista || val) : val)
      }
    })

    if (pedacos.length > 0) {
      item.value.sku = pedacos.join(tipoProdutoSelecionado.value.separadorCodigo || '').toUpperCase()
    } else {
      item.value.sku = ''
    }
  }
}, { deep: true })

const atributosFiscaisConfig = ref([])
const respostasFiscais = reactive({})
const descricaoNcm = ref('')

watch(() => item.value.codigoNcm, async (newCodigoNcm) => {
  if (!newCodigoNcm || naturezaSelecionada.value !== PROD_NATUREZA.ProdutoFisico) {
    atributosFiscaisConfig.value = []
    descricaoNcm.value = ''
    item.value.dataFimValidade = null
    item.value.idNcm = null
    return
  }

  try {
    const resNcm = await api.get(`/Ncms/por-codigo/${newCodigoNcm}`)
    descricaoNcm.value = resNcm.data.descricao
    item.value.idNcm = resNcm.data.idNcm || null

    if (resNcm.data.dataFimValidade !== undefined) {
      item.value.dataFimValidade = resNcm.data.dataFimValidade
    } else {
      item.value.dataFimValidade = null
    }

    const exigenciasProduto = (resNcm.data.atributos || []).filter(a => a.escopo === 'PRODUTO')
    atributosFiscaisConfig.value = exigenciasProduto

    exigenciasProduto.forEach(attr => {
      if (respostasFiscais[attr.idAtributoNcm] === undefined) {
        respostasFiscais[attr.idAtributoNcm] = ''
      }
    })
  } catch (error) {
    descricaoNcm.value = ''
    item.value.idNcm = null
    console.error('Erro ao carregar atributos CADA da NCM:', error)
  }
}, { immediate: true })

const onNcmSelecionada = (ncmObj) => {
  if (ncmObj) {
    descricaoNcm.value = ncmObj.descricao
    item.value.idNcm = ncmObj.idNcm || null
    item.value.codigoNcm = ncmObj.codigoNcm || ''
  }
}

const modalUnidade = ref(false)
const unidadeEmEdicao = ref(null)

const iniciarNovaUnidade = () => {
  if (isSaving.value) return
  unidadeEmEdicao.value = {
    _isNew: true,
    lstUnidadeMedida: '',
    qtdUnidadeMedida: 1,
    qtdUnidadeBase: 1,
    unidadePadraoVenda: false,
    descricaoMotivo: '',
    lstStatus: null
  }
  modalUnidade.value = true
}

const editarUnidade = (unid, idx) => {
  unidadeEmEdicao.value = { ...unid, _index: idx, _isNew: false }
  modalUnidade.value = true
}

const salvarUnidadeLocal = () => {
  const unid = unidadeEmEdicao.value
  unid.unidadePadraoVenda = unid.unidadePadraoVenda === true || String(unid.unidadePadraoVenda) === 'true'

  if (!unid.lstUnidadeMedida || !unid.qtdUnidadeMedida || !unid.qtdUnidadeBase || !unid.lstStatus) {
    emit('show-notification', { message: 'Preencha a Unidade, as quantidades da equação e o Status.', type: 'warning' })
    return
  }

  if (unid.unidadePadraoVenda) {
    item.value.unidadesConversao = item.value.unidadesConversao || []
    item.value.unidadesConversao.forEach(u => {
      u.unidadePadraoVenda = false
    })
  }

  const { _index, _isNew, ...dados } = unid
  item.value.unidadesConversao = item.value.unidadesConversao || []

  if (!_isNew && typeof _index === 'number') {
    item.value.unidadesConversao[_index] = dados
  } else {
    item.value.unidadesConversao.push(dados)
  }
  modalUnidade.value = false
}

const onUnidadeSelecionada = (unidadeModal) => {
  if (!unidadeModal) return
  const lista = listas.LstUnidadeMedida || []
  if (!lista.some(u => String(u.idListaClienteItem) === String(unidadeModal.idListaClienteItem))) {
    if (!listas.LstUnidadeMedida) {
      listas.LstUnidadeMedida = []
    }
    listas.LstUnidadeMedida.push(unidadeModal)
  }
}

const getListaObj = (idItem, listaRef) => {
  if (!idItem || !listaRef) return null
  return listaRef.find(i => String(i.idListaClienteItem) === String(idItem) || String(i.idListaSistemaItem) === String(idItem)) || null
}

const getTextoLista = (idItem, listaRef) => {
  const obj = getListaObj(idItem, listaRef)
  return obj ? (obj.textoLista || obj.descricaoBreve) : idItem
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['Produto', 'ProdutoConversaoUnidades', 'TipoProduto', 'ProdutoExigenciasLicenca', 'ProdutoNomeIdioma', 'ProdutoClausulasComerciais', 'ProdutoClienteClausula'])
    Object.assign(listas, result)

    // Listas SPED personalizáveis (não mapeadas no Dicionário de Metadados).
    const [codGen, exIpi] = await Promise.all([
      metadataStore.getListaLookup('PROD_SPED_COD_GEN', 'Cliente'),
      metadataStore.getListaLookup('PROD_SPED_EX_IPI', 'Cliente')
    ])
    listas.LstCodigoGenero = codGen || []
    listas.LstExIpi = exIpi || []
  } catch (error) {
    console.error('Erro ao carregar listas via metadados:', error)
  }
}

// Opções do Gênero do Item (SPED): lista personalizável + valor atual (dados legados).
const opcoesCodigoGenero = computed(() => {
  const base = listas.LstCodigoGenero || []
  const atual = item.value?.codigoGenero
  if (atual && !base.some(o => o.textoLista === atual)) {
    return [...base, { textoLista: atual }]
  }
  return base
})

// Opções da Exceção IPI (SPED): lista personalizável + valor atual (dados legados).
const opcoesExIpi = computed(() => {
  const base = listas.LstExIpi || []
  const atual = item.value?.exIpi
  if (atual && !base.some(o => o.textoLista === atual)) {
    return [...base, { textoLista: atual }]
  }
  return base
})

onMounted(async () => {
  await carregarListas()
  await resolverPlanoCategoria()

  try {
    const resTipos = await api.get('/TiposProduto?pageSize=100')
    tiposProdutoCache.value = resTipos.data.items || resTipos.data
  } catch (error) {
    console.error('Erro ao carregar tipos de produto:', error)
  }

  if (isEditing.value && item.value?.idProduto) {
    try {
      const resCompleta = await api.get(`/Produtos/${item.value.idProduto}`)
      Object.assign(item.value, resCompleta.data)

      if (item.value.atributosDinamicos) {
        item.value.atributosDinamicos.forEach(attr => {
          respostasComerciais[attr.prefixoItemLista] = attr.lstValorAtributo
        })
      }
      if (item.value.valoresAtributosFiscais) {
        item.value.valoresAtributosFiscais.forEach(attr => {
          respostasFiscais[attr.idAtributoNcm] = attr.valorInformado
        })
      }

      // Carrega as exigências de licença individualmente
      await carregarExigenciasLicenca(item.value.idProduto)
      await carregarNomesIdiomas(item.value.idProduto)
      await carregarClausulasComerciais(item.value.idProduto)
    } catch (err) {
      emit('show-notification', { message: 'Falha ao carregar os dados completos do item.', type: 'error' })
      console.error('Erro ao buscar Item Completo:', err)
    }
  }
})

const carregarExigenciasLicenca = async (idProduto) => {
  if (!idProduto) return
  try {
    const res = await api.get('/ProdutoExigenciasLicencas', {
      params: { idProduto, pageSize: 100 }
    })
    exigenciasLicenca.value = res.data.items || res.data || []
  } catch (error) {
    console.error('Erro ao carregar exigências de licença:', error)
    exigenciasLicenca.value = []
  }
}

const iniciarNovaExigencia = () => {
  if (!item.value.idProduto) {
    emit('show-notification', { message: 'Salve o produto antes de adicionar exigências.', type: 'warning' })
    return
  }
  exigenciaEmEdicao.value = {
    _isNew: true,
    idProduto: item.value.idProduto,
    lstOrgaoAnuente: '',
    lstModeloLPCO: '',
    lstTipoOperacaoComex: '',
    lstMomentoExigenciaLicenca: ''
  }
  modalExigencia.value = true
}

const editarExigencia = (ex, idx) => {
  exigenciaEmEdicao.value = {
    ...ex,
    _index: idx,
    _isNew: false
  }
  modalExigencia.value = true
}

const excluirExigencia = async (ex, idx) => {
  if (!ex.idProdutoExigenciasLicenca) {
    exigenciasLicenca.value.splice(idx, 1)
    return
  }
  try {
    await api.delete(`/ProdutoExigenciasLicencas/${ex.idProdutoExigenciasLicenca}`)
    exigenciasLicenca.value.splice(idx, 1)
    emit('show-notification', { message: 'Exigência excluída com sucesso.', type: 'success' })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao excluir exigência.', type: 'error' })
    console.error('Erro ao excluir exigência:', error)
  }
}

const salvarExigencia = async () => {
  const dados = exigenciaEmEdicao.value
  if (!dados.lstOrgaoAnuente || !dados.lstModeloLPCO || !dados.lstTipoOperacaoComex || !dados.lstMomentoExigenciaLicenca) {
    emit('show-notification', { message: 'Preencha todos os campos da exigência.', type: 'warning' })
    return
  }

  isSavingExigencia.value = true
  try {
    if (dados._isNew) {
      const res = await api.post('/ProdutoExigenciasLicencas', {
        idProduto: dados.idProduto,
        lstOrgaoAnuente: dados.lstOrgaoAnuente,
        lstModeloLPCO: dados.lstModeloLPCO,
        lstTipoOperacaoComex: dados.lstTipoOperacaoComex,
        lstMomentoExigenciaLicenca: dados.lstMomentoExigenciaLicenca
      })
      exigenciasLicenca.value.push(res.data)
      emit('show-notification', { message: 'Exigência adicionada com sucesso.', type: 'success' })
    } else {
      const res = await api.put(`/ProdutoExigenciasLicencas/${dados.idProdutoExigenciasLicenca}`, {
        lstOrgaoAnuente: dados.lstOrgaoAnuente,
        lstModeloLPCO: dados.lstModeloLPCO,
        lstTipoOperacaoComex: dados.lstTipoOperacaoComex,
        lstMomentoExigenciaLicenca: dados.lstMomentoExigenciaLicenca
      })
      if (typeof dados._index === 'number') {
        exigenciasLicenca.value[dados._index] = res.data
      }
      emit('show-notification', { message: 'Exigência atualizada com sucesso.', type: 'success' })
    }
    modalExigencia.value = false
  } catch (error) {
    emit('show-notification', { message: 'Erro ao salvar exigência.', type: 'error' })
    console.error('Erro ao salvar exigência:', error)
  } finally {
    isSavingExigencia.value = false
  }
}

// --- NOMES EM OUTROS IDIOMAS ---

const carregarNomesIdiomas = async (idProduto) => {
  if (!idProduto) return
  try {
    const res = await api.get('/ProdutoNomesIdiomas', {
      params: { idProduto, pageSize: 100 }
    })
    nomesIdiomas.value = res.data.items || res.data || []
  } catch (error) {
    console.error('Erro ao carregar nomes em idiomas:', error)
    nomesIdiomas.value = []
  }
}

const iniciarNovoNomeIdioma = () => {
  if (!item.value.idProduto) {
    emit('show-notification', { message: 'Salve o produto antes de adicionar nomes em outros idiomas.', type: 'warning' })
    return
  }
  nomeIdiomaEmEdicao.value = {
    _isNew: true,
    idProduto: item.value.idProduto,
    lstIdioma: '',
    nome: ''
  }
  modalNomeIdioma.value = true
}

const editarNomeIdioma = (item, idx) => {
  nomeIdiomaEmEdicao.value = {
    ...item,
    _index: idx,
    _isNew: false
  }
  modalNomeIdioma.value = true
}

const excluirNomeIdioma = async (item, idx) => {
  if (!item.idProdutoNomeIdioma) {
    nomesIdiomas.value.splice(idx, 1)
    return
  }
  try {
    await api.delete(`/ProdutoNomesIdiomas/${item.idProdutoNomeIdioma}`)
    nomesIdiomas.value.splice(idx, 1)
    emit('show-notification', { message: 'Nome excluído com sucesso.', type: 'success' })
  } catch (error) {
    emit('show-notification', { message: 'Erro ao excluir nome.', type: 'error' })
    console.error('Erro ao excluir nome:', error)
  }
}

const salvarNomeIdioma = async () => {
  const dados = nomeIdiomaEmEdicao.value
  if (!dados.lstIdioma || !dados.nome) {
    emit('show-notification', { message: 'Preencha o idioma e o nome.', type: 'warning' })
    return
  }

  isSavingNomeIdioma.value = true
  try {
    if (dados._isNew) {
      const res = await api.post('/ProdutoNomesIdiomas', {
        idProduto: dados.idProduto,
        lstIdioma: dados.lstIdioma,
        nome: dados.nome
      })
      nomesIdiomas.value.push(res.data)
      emit('show-notification', { message: 'Nome adicionado com sucesso.', type: 'success' })
    } else {
      const res = await api.put(`/ProdutoNomesIdiomas/${dados.idProdutoNomeIdioma}`, {
        lstIdioma: dados.lstIdioma,
        nome: dados.nome
      })
      if (typeof dados._index === 'number') {
        nomesIdiomas.value[dados._index] = res.data
      }
      emit('show-notification', { message: 'Nome atualizado com sucesso.', type: 'success' })
    }
    modalNomeIdioma.value = false
  } catch (error) {
    const data = error.response?.data
    let msg = data?.message
    if (!msg && data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    emit('show-notification', { message: msg || 'Erro ao salvar nome.', type: 'error' })
    console.error('Erro ao salvar nome:', error)
  } finally {
    isSavingNomeIdioma.value = false
  }
}

// --- CLÁUSULAS COMERCIAIS ---

const carregarClausulasComerciais = async (idProduto) => {
  if (!idProduto) return
  try {
    const res = await api.get(`/ProdutoClausulasComerciais/produto/${idProduto}`)
    clausulasComerciais.value = res.data || []
  } catch (error) {
    console.error('Erro ao carregar cláusulas comerciais:', error)
  }
}

const iniciarNovaClausula = () => {
  if (!item.value.idProduto) {
    emit('show-notification', {
      message: 'Salve o produto antes de adicionar cláusulas comerciais.',
      type: 'warning'
    })
    return
  }
  clausulaEmEdicao.value = {
    _isNew: true,
    idProduto: item.value.idProduto,
    titulo: '',
    texto: '',
    ordemExibicao: null,
    isDefinitiva: false,
    isFixa: false,
    lstStatusClausula: '',
    lstAplicacaoClausula: '',
    lstVisibilidade: ''
  }
  modalClausula.value = true
}

const editarClausula = (item, idx) => {
  clausulaEmEdicao.value = {
    ...item,
    _index: idx,
    _isNew: false
  }
  modalClausula.value = true
}

const salvarClausula = async () => {
  const dados = clausulaEmEdicao.value
  if (!dados.titulo || !dados.texto || !dados.lstAplicacaoClausula) {
    emit('show-notification', { message: 'Preencha todos os campos obrigatórios.', type: 'warning' })
    return
  }
  isSavingClausula.value = true
  try {
    if (dados._isNew) {
      const res = await api.post('/ProdutoClausulasComerciais', {
        idProduto: dados.idProduto,
        titulo: dados.titulo,
        texto: dados.texto,
        ordemExibicao: dados.ordemExibicao,
        isDefinitiva: dados.isDefinitiva,
        isFixa: dados.isFixa,
        lstStatusClausula: dados.lstStatusClausula,
        lstAplicacaoClausula: dados.lstAplicacaoClausula,
        lstVisibilidade: dados.lstVisibilidade
      })
      clausulasComerciais.value.push(res.data)
    } else {
      const res = await api.put(`/ProdutoClausulasComerciais/${dados.idProdutoClausulasComerciais}`, {
        titulo: dados.titulo,
        texto: dados.texto,
        ordemExibicao: dados.ordemExibicao,
        isDefinitiva: dados.isDefinitiva,
        isFixa: dados.isFixa,
        lstStatusClausula: dados.lstStatusClausula,
        lstAplicacaoClausula: dados.lstAplicacaoClausula,
        lstVisibilidade: dados.lstVisibilidade,
        xmin: dados.xmin
      })
      if (typeof dados._index === 'number') {
        clausulasComerciais.value[dados._index] = res.data
      }
    }
    modalClausula.value = false
    emit('show-notification', { message: 'Cláusula comercial salva com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar cláusula comercial.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingClausula.value = false
  }
}

const getClasseStatusClausula = (status) => {
  if (!status) return 'badge-ghost-secondary'
  const ativo = (listas.LstStatusClausula || []).find(s =>
    String(s.idListaClienteItem) === String(status) &&
    s.textoLista?.toUpperCase().includes('ATIVO')
  )
  return ativo ? 'badge-ghost-success' : 'badge-ghost-secondary'
}

// --- CLÁUSULAS POR CLIENTE (ProdutoClienteClausula) ---

const confBuscaCliente = computed(() =>
  criarConfigBuscaCliente(authStore.organizacaoAtiva)
)

const carregarClausulasPorCliente = async () => {
  if (!item.value.idProduto) return
  try {
    const res = await api.get(`/ProdutoClienteClausulas/por-produto-cliente/${item.value.idProduto}`)
    clausulasPorCliente.value = res.data || []
  } catch (error) {
    console.error('Erro ao carregar cláusulas por cliente:', error)
    clausulasPorCliente.value = []
  }
}

watch(toggleClausula, async (novoValor) => {
  if (novoValor === 'por-cliente' && item.value.idProduto) {
    await carregarClausulasPorCliente()
  }
})

const iniciarNovaClausulaCliente = () => {
  if (!item.value.idProduto) {
    emit('show-notification', {
      message: 'Salve o produto antes de adicionar cláusulas por cliente.',
      type: 'warning'
    })
    return
  }
  clausulaClienteEmEdicao.value = {
    _isNew: true,
    idProduto: item.value.idProduto,
    idParceiro: null,
    titulo: '',
    texto: '',
    ordemExibicao: null,
    isDefinitiva: false,
    lstStatusClausula: '',
    lstVisibilidade: '',
    lstAplicacaoClausula: ''
  }
  modalClausulaCliente.value = true
}

const editarClausulaCliente = (item, idx) => {
  clausulaClienteEmEdicao.value = {
    ...item,
    _index: idx,
    _isNew: false
  }
  modalClausulaCliente.value = true
}

const salvarClausulaCliente = async () => {
  const dados = clausulaClienteEmEdicao.value
  if (!dados.titulo || !dados.texto || !dados.idParceiro) {
    emit('show-notification', { message: 'Preencha o título, texto e cliente.', type: 'warning' })
    return
  }
  isSavingClausulaCliente.value = true
  try {
    if (dados._isNew) {
      const res = await api.post('/ProdutoClienteClausulas', {
        idProduto: dados.idProduto,
        idParceiro: dados.idParceiro,
        titulo: dados.titulo,
        texto: dados.texto,
        ordemExibicao: dados.ordemExibicao,
        isDefinitiva: dados.isDefinitiva,
        lstStatusClausula: dados.lstStatusClausula,
        lstVisibilidade: dados.lstVisibilidade,
        lstAplicacaoClausula: dados.lstAplicacaoClausula
      })
      clausulasPorCliente.value.push(res.data)
    } else {
      const res = await api.put(`/ProdutoClienteClausulas/${dados.idProdutoClienteClausula}`, {
        titulo: dados.titulo,
        texto: dados.texto,
        ordemExibicao: dados.ordemExibicao,
        isDefinitiva: dados.isDefinitiva,
        lstStatusClausula: dados.lstStatusClausula,
        lstVisibilidade: dados.lstVisibilidade,
        lstAplicacaoClausula: dados.lstAplicacaoClausula,
        xmin: dados.xmin
      })
      if (typeof dados._index === 'number') {
        clausulasPorCliente.value[dados._index] = res.data
      }
    }
    modalClausulaCliente.value = false
    emit('show-notification', { message: 'Cláusula por cliente salva com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar cláusula por cliente.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isSavingClausulaCliente.value = false
  }
}

const excluirClausulaCliente = async (item, idx) => {
  if (!item.idProdutoClienteClausula) {
    clausulasPorCliente.value.splice(idx, 1)
    return
  }
  try {
    await api.delete(`/ProdutoClienteClausulas/${item.idProdutoClienteClausula}`)
    clausulasPorCliente.value.splice(idx, 1)
    emit('show-notification', { message: 'Cláusula por cliente excluída com sucesso.', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao excluir cláusula por cliente.'
    if (typeof data === 'string') msg = data
    else if (data?.message) msg = data.message
    else if (data?.title) msg = data.title
    else if (data?.errors) msg = Object.values(data.errors).flat().join('; ')
    emit('show-notification', { message: msg, type: 'error' })
  }
}

// ============================================================
// HELPER: Monta payload completo do produto (reutilizável)
// ============================================================
const montarPayloadProduto = () => {
  const atributosDinamicosPayload = Object.keys(respostasComerciais)
    .filter(prefixo => respostasComerciais[prefixo] !== '')
    .map(prefixo => ({
      prefixoItemLista: prefixo,
      lstValorAtributo: respostasComerciais[prefixo]
    }))

  let atributosFiscaisPayload = []
  if (naturezaSelecionada.value === PROD_NATUREZA.ProdutoFisico) {
    atributosFiscaisPayload = Object.keys(respostasFiscais)
      .filter(idAtributoNcm => respostasFiscais[idAtributoNcm] !== '')
      .map(idAtributoNcm => ({
        idAtributoNcm: Number(idAtributoNcm),
        valorInformado: respostasFiscais[idAtributoNcm]
      }))
  }

  let skuEnviar = item.value.sku
  if (!isSkuManual.value && !isEditing.value) {
    if (tipoProdutoSelecionado.value?.formatoGeracaoSku === 'ATRIBUTOS') {
      if (skuEnviar && skuEnviar.length > 50) {
        emit('show-notification', {
          message: `O código SKU gerado (${skuEnviar.length} caracteres) excede o limite máximo de 50.`,
          type: 'error'
        })
        return null
      }
    } else {
      skuEnviar = ''
    }
  }

  // Resolve o Label da Origem a gravar no Banco se for Produto Físico
  let descOrigem = ''
  if (naturezaSelecionada.value === PROD_NATUREZA.ProdutoFisico && item.value.codigoOrigemMercadoria) {
    const origemSelecionada = origensMercadoria.find(o => o.id === item.value.codigoOrigemMercadoria)
    if (origemSelecionada) descOrigem = origemSelecionada.label
  }

  return {
    ...item.value,
    sku: skuEnviar,
    reterIssPadrao: item.value.reterIssPadrao === true || String(item.value.reterIssPadrao) === 'true',
    atributosDinamicos: atributosDinamicosPayload,
    valoresAtributosFiscais: atributosFiscaisPayload,
    unidadesConversao: item.value.unidadesConversao || [],
    idCategoriaFinanceiraEntrada: item.value.idCategoriaFinanceiraEntrada || null,
    idCategoriaFinanceiraSaida: item.value.idCategoriaFinanceiraSaida || null,
    // Resetar campos não aplicáveis conforme natureza
    idNcm: naturezaSelecionada.value === PROD_NATUREZA.Servico ? null : (item.value.idNcm || null),
    codigoNcm: naturezaSelecionada.value === PROD_NATUREZA.Servico ? null : item.value.codigoNcm,
    codigoOrigemMercadoria: naturezaSelecionada.value === PROD_NATUREZA.Servico ? null : item.value.codigoOrigemMercadoria,
    descricaoOrigemMercadoria: naturezaSelecionada.value === PROD_NATUREZA.Servico ? null : descOrigem,
    codigoGenero: naturezaSelecionada.value === PROD_NATUREZA.Servico ? null : item.value.codigoGenero,
    exIpi: naturezaSelecionada.value === PROD_NATUREZA.Servico ? null : item.value.exIpi,
    lstCodigoLc116: naturezaSelecionada.value === PROD_NATUREZA.ProdutoFisico ? null : item.value.lstCodigoLc116,
    lstCodigoNbs: naturezaSelecionada.value === PROD_NATUREZA.ProdutoFisico ? null : item.value.lstCodigoNbs
  }
}

const salvarProduto = async () => {
  const payload = montarPayloadProduto()
  if (!payload) return // null significa erro de validação (SKU > 50)
  await submitForm('Produtos', 'idProduto', payload)
}
</script>

<template>
  <FormularioCadastro
    module-name="Cadastro de Produtos/Serviços"
    :is-loading="isSaving"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    :ocultar-salvar="!isProdutoSaved"
    :desabilitar-salvar-alteracoes="isFramesSecundariosBloqueados"
    @submit="salvarProduto"
    @submit-continuar="salvarAlteracoes"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
  >
    <template #acoes-header v-if="isEditing">
      <button
        type="button"
        class="btn-icon-minimalista me-3"
        @click="mostrarAuditoria = true"
        title="Ver Histórico de Alterações"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="!isPlanoCompliance" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3 text-warning"></i>
      <div><strong>Atenção:</strong> O plano atual da empresa não exige o preenchimento da Classificação Fiscal. Contudo, dados incompletos podem limitar o Motor Fiscal no futuro.</div>
    </div>

    <!-- FRAME 1: DADOS BÁSICOS -->
    <div class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('basico')">
        <h5 class="mb-0"><i class="bi bi-box-seam me-2"></i>Dados Básicos</h5>
        <i class="bi" :class="accordionState.basico ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
      </div>

      <div v-show="accordionState.basico" class="card-body">

        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <CampoBusca
              id="idTipoProduto"
              label="Tipo de Item (Categoria)"
              v-model="item.idTipoProduto"
              :configuracao-busca="confBuscaTipoProduto"
              required
              :disabled="isEditing"
            />
          </div>
          <div v-if="tipoProdutoSelecionado" class="col-md-8 d-flex align-items-center">
             <span class="badge bg-body-secondary text-body border border-secondary-subtle fw-normal fs-6">
                <i :class="naturezaSelecionada === PROD_NATUREZA.Servico ? 'bi bi-tools text-primary' : 'bi bi-box-seam text-success'"></i>
                Identificado como: <strong>{{ getTextoLista(naturezaSelecionada, listas.LstNaturezaItem) }}</strong>
             </span>
          </div>
        </div>

        <div class="row g-3 mb-4" v-if="tipoProdutoSelecionado && tipoProdutoSelecionado.formatoGeracaoSku === 'ATRIBUTOS' && atributosComerciaisSku.length > 0">
          <div class="col-12">
            <div class="p-3 border border-primary-subtle rounded bg-primary-subtle bg-opacity-10 shadow-sm">
               <span class="d-block fw-bold text-primary-emphasis mb-3"><i class="bi bi-upc-scan me-2"></i> Atributos que formam o Código (SKU)</span>
               <div class="row g-3">
                 <div class="col-md-4" v-for="attr in atributosComerciaisSku" :key="attr.idCabecalho">
                    <CampoBuscaLista
                      :id="'attr_sku_'+attr.idCabecalho"
                      :label="attr.nomeLista"
                      v-model="respostasComerciais[attr.idCabecalho]"
                      :options="attr.opcoes"
                      :prefixo="attr.idCabecalho"
                      required
                      :disabled="isEditing"
                    />
                 </div>
               </div>
            </div>
          </div>
        </div>

        <div class="row g-3 mb-4">
          <div class="col-md-3">
            <CampoInput
              id="sku"
              label="SKU (Código Interno)"
              v-model="item.sku"
              :required="isSkuManual && !isEditing"
              :readonly="isEditing || !isSkuManual"
              :disabled="isEditing || !isSkuManual"
              :placeholder="isEditing ? 'SKU não pode ser alterado' : (isSkuManual ? 'Ex: P-001' : (tipoProdutoSelecionado?.formatoGeracaoSku === 'SEQUENCIAL' ? 'Gerado Sequencialmente' : 'Gerado Pelos Atributos'))"
              maxlength="50"
            />
            <div v-if="!isSkuManual && item.sku && item.sku.length > 50" class="text-danger small mt-1">
              <i class="bi bi-exclamation-triangle-fill me-1"></i> Limite excedido ({{ item.sku.length }}/50)
            </div>
          </div>
          <div class="col-md-9">
            <CampoInput id="nome" label="Descrição Comercial do Item" v-model="item.nome" required maxlength="200" />
          </div>
        </div>

        <template v-if="atributosComerciaisGerais.length > 0">
          <h6 class="text-muted text-uppercase small fw-bold mb-3 border-bottom pb-1 border-secondary-subtle">Características do Item</h6>
          <div class="row g-3 mb-4">
            <div class="col-md-4" v-for="attr in atributosComerciaisGerais" :key="attr.idCabecalho">
              <CampoBuscaLista
                :id="'attr_com_'+attr.idCabecalho"
                :label="attr.nomeLista"
                v-model="respostasComerciais[attr.idCabecalho]"
                :options="attr.opcoes"
                :prefixo="attr.idCabecalho"
              />
            </div>
          </div>
        </template>

        <h6 class="text-muted text-uppercase small fw-bold mb-3 border-bottom pb-1 border-secondary-subtle">Códigos Auxiliares</h6>
        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <CampoInput id="codigoBarras" label="Código de Barras (EAN/GTIN)" v-model="item.codigoBarras" maxlength="50" />
          </div>
          <div class="col-md-4">
            <CampoInput id="refMercado" label="Cód. Ref. Mercado (Opcional)" v-model="item.codigoReferenciaMercado" maxlength="100" />
          </div>
          <div class="col-md-4">
            <CampoInput id="sistAntigo" label="Cód. Sistema Antigo (Legado)" v-model="item.codigoSistemaAntigo" maxlength="100" />
          </div>
        </div>

        <h6 class="text-muted text-uppercase small fw-bold mb-3 border-bottom pb-1 border-secondary-subtle">Configuração Comercial</h6>
        <div class="row g-3 mb-4">
          <div class="col-md-3">
            <CampoInput
              id="margemEsperadaPercentual"
              label="Margem Esperada (%)"
              type="decimal"
              :casas-decimais="2"
              min="0"
              max="100"
              v-model.number="item.margemEsperadaPercentual"
              descricao-informativa="Percentual de margem esperada específica para este produto. Tem precedência sobre o Tipo de Produto e a Empresa."
            />
          </div>
        </div>

        <div class="border-top border-secondary-subtle pt-3">
          <div class="row g-3">
            <!-- GUARDIÃO: Unidade inserida nos Dados Básicos para Serviços -->
            <div class="col-md-3" v-if="naturezaSelecionada === PROD_NATUREZA.Servico">
              <CampoBuscaLista
                id="lstUnidadeMedidaBase"
                label="Unidade Medida Base"
                v-model="item.lstUnidadeMedidaBase"
                tabela="Produto"
                @selecionado="onUnidadeSelecionada"
                required
              />
            </div>
            <div class="col-md-3">
              <CampoSelect id="lstStatus" label="Status" v-model="item.lstStatus" :options="listas.LstStatus || []" option-value="idListaClienteItem" option-label="textoLista" required />
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Botão Salvar e Continuar (apenas criação) -->
    <div class="mb-4" v-if="!isProdutoSaved">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarProdutoLocalmente"
        :disabled="isSavingProdutoLocal || !item.nome"
      >
        <span v-if="isSavingProdutoLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Produto e Liberar Atributos
      </button>
    </div>

    <!-- FRAME INTEGRAÇÃO FINANCEIRA -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center pointer bg-success-subtle" @click="toggleAccordion('financeiro')">
          <h5 class="mb-0 text-success-emphasis"><i class="bi bi-cash-coin me-2"></i>Integração Financeira</h5>
          <i class="bi text-success-emphasis" :class="accordionState.financeiro ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.financeiro" class="card-body border-top border-success-subtle">
          <div class="alert alert-success border-0 shadow-sm small py-2 mb-4 d-flex align-items-center">
            <i class="bi bi-info-circle-fill me-2 fs-5"></i>
            <span>As categorias listadas aqui serão adotadas como o padrão na geração de faturamento ou despesas (compras). Você pode substituí-las.</span>
          </div>
          <div class="row g-3">
            <div class="col-md-5">
              <CampoBusca
                id="idCategoriaFinanceiraEntrada"
                label="Categoria Financeira (Entradas/Receitas)"
                v-model="item.idCategoriaFinanceiraEntrada"
                :configuracao-busca="confBuscaCatEntrada"
                :required="isComercializavel"
              />
            </div>
            <div class="col-md-5">
              <CampoBusca
                id="idCategoriaFinanceiraSaida"
                label="Categoria Financeira (Saídas/Despesas)"
                v-model="item.idCategoriaFinanceiraSaida"
                :configuracao-busca="confBuscaCatSaida"
                :required="isCompravel"
              />
            </div>
          </div>
          <div class="row g-3 mt-2">
            <div class="col-md-5">
              <CampoBuscaLista
                id="lstTipoTaxaConversaoPadrao"
                label="Tipo de Taxa de Conversão Padrão"
                v-model="item.lstTipoTaxaConversaoPadrao"
                tabela="Produto"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME 2: LOGÍSTICA (Apenas Físico) -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
      <div class="card mb-3" v-if="naturezaSelecionada === PROD_NATUREZA.ProdutoFisico">
        <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('logistica')">
          <h5 class="mb-0"><i class="bi bi-truck me-2"></i>Dados Logísticos</h5>
          <i class="bi" :class="accordionState.logistica ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.logistica" class="card-body">
          <div class="row g-3">
            <div class="col-md-3">
              <CampoBuscaLista
                id="lstUnidadeMedidaBase"
                label="Unidade Base (Estoque)"
                v-model="item.lstUnidadeMedidaBase"
                tabela="Produto"
                @selecionado="onUnidadeSelecionada"
                required
              />
            </div>
            <div class="col-md-3">
              <CampoBuscaLista
                id="lstUnidadeMedidaVolume"
                label="Unidade Medida de Volume (Logística)"
                v-model="item.lstUnidadeMedidaVolume"
                tabela="Produto"
                :disabled="!isVolumeEnabled"
                descricao-informativa="Ex: M³, Litro, Galão. Se preenchido, exige conversão ativa no grid abaixo."
              />
              <div v-if="!isVolumeEnabled" class="text-muted small mt-1">
                <i class="bi bi-info-circle me-1"></i> Ative "Gerir Estoque por Unidade Logística" no Tipo de Produto para habilitar.
              </div>
            </div>
            <div class="col-md-3">
              <CampoInput id="pesoBruto" label="Peso Bruto Base (KG)" type="number" step="0.001" min="0" v-model.number="item.pesoBruto" />
            </div>
            <div class="col-md-3">
              <CampoInput id="pesoLiquido" label="Peso Líquido Base (KG)" type="number" step="0.001" min="0" v-model.number="item.pesoLiquido" />
            </div>
            <div class="col-md-3">
              <CampoInput id="cubagem" label="Cubagem Base (CBM/m³)" type="number" step="0.0001" min="0" v-model.number="item.cubagem" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME 3: FISCAL (Reativo a Natureza) -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('fiscal')">
          <h5 class="mb-0"><i class="bi bi-globe-americas me-2"></i>Classificação Fiscal</h5>
          <i class="bi" :class="accordionState.fiscal ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.fiscal" class="card-body">

          <!-- BLOCO 3A: PRODUTO FÍSICO -->
          <div class="row g-3" v-if="naturezaSelecionada === PROD_NATUREZA.ProdutoFisico">
            <div class="col-md-4">
              <CampoBusca
                id="codigoNcm"
                label="NCM (Nomenclatura Comum do Mercosul)"
                v-model="item.idNcm"
                :descricao-informativa="descricaoNcm"
                :configuracao-busca="ncmSearch"
                @selecionado="onNcmSelecionada"
                :required="isPlanoCompliance"
              />
              <div v-if="item.dataFimValidade" class="alert alert-danger mt-2 py-2 mb-0 d-flex align-items-center">
                <i class="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
                <div>
                  <strong>Atenção:</strong> NCM revogada em {{ new Date(item.dataFimValidade).toLocaleDateString('pt-BR') }}.
                </div>
              </div>
            </div>
            <div class="col-md-8">
              <CampoSelect id="codigoOrigemMercadoria" label="Origem da Mercadoria (SEFAZ/CST)" v-model="item.codigoOrigemMercadoria" :options="origensMercadoria" option-value="id" option-label="label" :required="isPlanoCompliance" />
            </div>
            <div class="col-md-2">
              <CampoSelect id="codigoGenero" label="Gênero do Item (SPED)" v-model="item.codigoGenero" :options="opcoesCodigoGenero" option-value="textoLista" option-label="textoLista" />
            </div>
            <div class="col-md-2">
              <CampoSelect id="exIpi" label="Exceção IPI (SPED)" v-model="item.exIpi" :options="opcoesExIpi" option-value="textoLista" option-label="textoLista" />
            </div>

            <div class="col-12 mt-4">
               <div class="p-3 border border-warning-subtle rounded bg-warning-subtle bg-opacity-10 shadow-sm">
                  <span class="d-block fw-bold text-warning-emphasis mb-3"><i class="bi bi-bank me-2"></i> Exigências do Portal Único (Siscomex CADA)</span>

                  <div v-if="atributosFiscaisConfig.length === 0" class="text-muted small fst-italic">
                    <i class="bi bi-info-circle me-1"></i> Selecione uma NCM para visualizar as exigências de importação/exportação.
                  </div>

                  <div v-else class="row g-3">
                     <div class="col-md-4" v-for="attr in atributosFiscaisConfig" :key="attr.idAtributoNcm">
                          <CampoInput
                            :id="'attr_fis_'+attr.idAtributoNcm"
                            :label="`Cód: ${attr.codigoSiscomex}`"
                            v-model="respostasFiscais[attr.idAtributoNcm]"
                            :required="attr.obrigatorio"
                            :descricao-informativa="attr.descricao"
                          />
                       </div>
                  </div>
               </div>
            </div>
          </div>

          <!-- BLOCO 3B: SERVIÇO -->
          <div class="row g-3" v-if="naturezaSelecionada === PROD_NATUREZA.Servico">
            <div class="col-md-5">
              <CampoBuscaLista
                id="lstCodigoLc116"
                label="Código LC 116/03 - ISS Municipal"
                v-model="item.lstCodigoLc116"
                tabela="Produto"
                :required="isPlanoCompliance"
              />
            </div>
            <div class="col-md-5">
              <CampoBuscaLista
                id="lstCodigoNbs"
                label="Código NBS - Federal/Comex"
                v-model="item.lstCodigoNbs"
                tabela="Produto"
                :required="isPlanoCompliance"
              />
            </div>
            <div class="col-md-2">
              <CampoSelect
                id="reterIssPadrao"
                label="Reter ISS Padrão?"
                v-model="item.reterIssPadrao"
                :options="simNaoOptions"
                option-value="id"
                option-label="label"
                required
              />
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- FRAME: EXIGÊNCIAS DE LPCO/LI -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
      <div class="card mb-3" v-if="naturezaSelecionada === PROD_NATUREZA.ProdutoFisico">
        <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('exigenciasLicenca')">
          <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2"></i>Exigências de LPCO/LI</h5>
          <i class="bi" :class="accordionState.exigenciasLicenca ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.exigenciasLicenca" class="card-body">
          <div class="admin-table-container border-0 shadow-none mb-0">
            <table class="table admin-table m-0">
              <thead>
                <tr>
                  <th>Órgão Anuente</th>
                  <th>Modelo LPCO</th>
                  <th>Operação Comex Exigida</th>
                  <th>Momento da Exigência</th>
                  <th class="text-end" style="width: 100px;">
                    <button type="button" class="btn btn-sm btn-primary py-0" @click="iniciarNovaExigencia" :disabled="!item.idProduto">
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(ex, idx) in exigenciasLicenca" :key="ex.idProdutoExigenciasLicenca || idx">
                  <td>{{ getTextoLista(ex.lstOrgaoAnuente, listas.LstOrgaoAnuente || []) }}</td>
                  <td>{{ getTextoLista(ex.lstModeloLPCO, listas.LstModeloLPCO || []) }}</td>
                  <td>{{ getTextoLista(ex.lstTipoOperacaoComex, listas.LstTipoOperacaoComex || []) }}</td>
                  <td>{{ getTextoLista(ex.lstMomentoExigenciaLicenca, listas.LstMomentoExigenciaLicenca || []) }}</td>
                  <td class="acoes-cell text-end pe-3">
                    <button type="button" class="btn-icon" @click="editarExigencia(ex, idx)"><i class="bi bi-pencil-fill"></i></button>
                    <button type="button" class="btn-icon text-danger" @click="excluirExigencia(ex, idx)"><i class="bi bi-trash-fill"></i></button>
                  </td>
                </tr>
                <tr v-if="exigenciasLicenca.length === 0">
                  <td colspan="5" class="text-center py-4 text-muted">Nenhuma exigência de LPCO/LI cadastrada.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME: NOMES EM OUTROS IDIOMAS -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('nomesIdiomas')">
          <h5 class="mb-0"><i class="bi bi-translate me-2"></i>Nomes em Outros Idiomas</h5>
          <i class="bi" :class="accordionState.nomesIdiomas ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.nomesIdiomas" class="card-body">
          <div class="admin-table-container border-0 shadow-none mb-0">
            <table class="table admin-table m-0">
              <thead>
                <tr>
                  <th>Idioma</th>
                  <th>Nome</th>
                  <th class="text-end" style="width: 100px;">
                    <button type="button" class="btn btn-sm btn-primary py-0" @click="iniciarNovoNomeIdioma" :disabled="!item.idProduto">
                      <i class="bi bi-plus-lg"></i>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(ni, idx) in nomesIdiomas" :key="ni.idProdutoNomeIdioma || idx">
                  <td>{{ getTextoLista(ni.lstIdioma, listas.LstIdioma) }}</td>
                  <td>{{ ni.nome }}</td>
                  <td class="acoes-cell text-end pe-3">
                    <button type="button" class="btn-icon" @click="editarNomeIdioma(ni, idx)"><i class="bi bi-pencil-fill"></i></button>
                    <button type="button" class="btn-icon text-danger" @click="excluirNomeIdioma(ni, idx)"><i class="bi bi-trash-fill"></i></button>
                  </td>
                </tr>
                <tr v-if="nomesIdiomas.length === 0">
                  <td colspan="3" class="text-center py-4 text-muted">Nenhum nome em outro idioma cadastrado.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Frame: Cláusulas Comerciais -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
      <div class="card mb-3">
        <div
          class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
          @click="toggleAccordion('clausulasComerciais')"
        >
          <h5 class="mb-0"><i class="bi bi-file-earmark-text me-2 text-primary"></i>Cláusulas Comerciais</h5>
          <div class="d-flex align-items-center gap-2" @click.stop>
            <div class="btn-group btn-group-sm">
              <button
                type="button"
                class="btn"
                :class="toggleClausula === 'gerais' ? 'btn-primary' : 'btn-outline-primary'"
                @click="toggleClausula = 'gerais'"
              >
                <i class="bi bi-building me-1"></i>Cláusulas Gerais
              </button>
              <button
                type="button"
                class="btn"
                :class="toggleClausula === 'por-cliente' ? 'btn-success' : 'btn-outline-success'"
                @click="toggleClausula = 'por-cliente'"
              >
                <i class="bi bi-people me-1"></i>Cláusulas Por Cliente
              </button>
            </div>
            <i :class="['bi', accordionState.clausulasComerciais ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
        </div>
        <div v-show="accordionState.clausulasComerciais" class="card-body">

          <!-- Grid: Cláusulas Gerais -->
          <div v-if="toggleClausula === 'gerais'">
            <div class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Ordem</th>
                    <th>Definitiva</th>
                    <th>Fixa</th>
                    <th>Aplicação</th>
                    <th>Visibilidade</th>
                    <th>Status</th>
                    <th class="text-end" style="width: 100px;">
                      <button
                        type="button"
                        class="btn btn-sm btn-primary py-0"
                        @click="iniciarNovaClausula"
                        :disabled="!item.idProduto"
                        title="Nova Cláusula"
                      >
                        <i class="bi bi-plus-lg"></i>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(cl, idx) in clausulasComerciais" :key="cl.idProdutoClausulasComerciais || idx">
                    <td>{{ cl.titulo }}</td>
                    <td>{{ cl.ordemExibicao }}</td>
                    <td>
                      <i :class="cl.isDefinitiva ? 'bi bi-check-circle-fill text-success' : 'bi bi-x-circle-fill text-secondary'"></i>
                    </td>
                    <td>
                      <i :class="cl.isFixa ? 'bi bi-check-circle-fill text-success' : 'bi bi-x-circle-fill text-secondary'"></i>
                    </td>
                    <td>{{ getTextoLista(cl.lstAplicacaoClausula, listas.LstAplicacaoClausula) }}</td>
                    <td>{{ getTextoLista(cl.lstVisibilidade, listas.LstVisibilidade) }}</td>
                    <td>
                      <span :class="['badge', getClasseStatusClausula(cl.lstStatusClausula)]">
                        {{ getTextoLista(cl.lstStatusClausula, listas.LstStatusClausula) }}
                      </span>
                    </td>
                    <td class="acoes-cell text-end pe-3">
                      <button type="button" class="btn-icon" @click="editarClausula(cl, idx)" title="Editar">
                        <i class="bi bi-pencil-fill"></i>
                      </button>
                      <!-- ❌ SEM botão de delete -->
                    </td>
                  </tr>
                  <tr v-if="clausulasComerciais.length === 0">
                    <td colspan="8" class="text-center py-4 text-muted">
                      Nenhuma cláusula comercial cadastrada para este produto.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Grid: Cláusulas Por Cliente -->
          <div v-if="toggleClausula === 'por-cliente'">
            <div class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Cliente</th>
                    <th>Ordem</th>
                    <th>Definitiva</th>
                    <th>Aplicação</th>
                    <th>Visibilidade</th>
                    <th>Status</th>
                    <th class="text-end" style="width: 100px;">
                      <button
                        type="button"
                        class="btn btn-sm btn-primary py-0"
                        @click="iniciarNovaClausulaCliente"
                        :disabled="!item.idProduto"
                        title="Nova Cláusula por Cliente"
                      >
                        <i class="bi bi-plus-lg"></i>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(cl, idx) in clausulasPorCliente" :key="cl.idProdutoClienteClausula || idx">
                    <td>{{ cl.titulo }}</td>
                    <td>{{ cl.apelidoCliente || cl.nomeCliente || 'Cliente #' + cl.idParceiro }}</td>
                    <td>{{ cl.ordemExibicao }}</td>
                    <td>
                      <i :class="cl.isDefinitiva ? 'bi bi-check-circle-fill text-success' : 'bi bi-x-circle-fill text-secondary'"></i>
                    </td>
                    <td>{{ getTextoLista(cl.lstAplicacaoClausula, listas.LstAplicacaoClausula) }}</td>
                    <td>{{ getTextoLista(cl.lstVisibilidade, listas.LstVisibilidade) }}</td>
                    <td>
                      <span :class="['badge', getClasseStatusClausula(cl.lstStatusClausula)]">
                        {{ getTextoLista(cl.lstStatusClausula, listas.LstStatusClausula) }}
                      </span>
                    </td>
                    <td class="acoes-cell text-end pe-3">
                      <button type="button" class="btn-icon" @click="editarClausulaCliente(cl, idx)" title="Editar">
                        <i class="bi bi-pencil-fill"></i>
                      </button>
                      <button type="button" class="btn-icon text-danger" @click="excluirClausulaCliente(cl, idx)" title="Excluir">
                        <i class="bi bi-trash-fill"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="clausulasPorCliente.length === 0">
                    <td colspan="8" class="text-center py-4 text-muted">
                      Nenhuma cláusula por cliente cadastrada para este produto.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- FRAME 4: UNIDADES -->
    <div :class="{ 'pe-none opacity-50': isFramesSecundariosBloqueados }">
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center pointer" @click="toggleAccordion('unidades')">
          <h5 class="mb-0"><i class="bi bi-calculator me-2"></i>Unidades de Conversão (Saída)</h5>
          <i class="bi" :class="accordionState.unidades ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
        </div>
        <div v-show="accordionState.unidades" class="card-body p-0 border-top border-secondary-subtle">
          <div class="admin-table-container border-0 shadow-none mb-0">
            <table class="table admin-table m-0">
              <thead>
                <tr>
                  <th style="width: 18%;">Unidade de Conversão</th>
                  <th>Equação de Conversão</th>
                  <th style="width: 120px;" class="text-center">Padrão?</th>
                  <th style="width: 120px;" class="text-center">Status</th>
                  <th>Motivo</th>
                  <th class="text-end" style="width: 100px;">
                    <button type="button" class="btn btn-sm btn-primary py-0" @click.stop="iniciarNovaUnidade"><i class="bi bi-plus-lg"></i></button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(unid, idx) in (item.unidadesConversao || [])" :key="idx">
                  <td class="fw-bold">{{ getTextoLista(unid.lstUnidadeMedida, listas.LstUnidadeMedida || []) }}</td>
                  <td class="font-monospace">
                    <span class="text-primary">{{ unid.qtdUnidadeMedida }}</span> [{{ getTextoLista(unid.lstUnidadeMedida, listas.LstUnidadeMedida || []) }}] =
                    <span class="text-success">{{ unid.qtdUnidadeBase }}</span> [{{ getTextoLista(item.lstUnidadeMedidaBase, listas.LstUnidadeMedidaBase || []) }}]
                  </td>
                  <td class="text-center">
                    <i v-if="unid.unidadePadraoVenda" class="bi bi-star-fill text-warning"></i>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td class="text-center">
                    <span class="badge" :class="obterClasseStatusGhost(getTextoLista(unid.lstStatus, listas.LstStatus || []))">
                      {{ getTextoLista(unid.lstStatus, listas.LstStatus || []) }}
                    </span>
                  </td>
                  <td>{{ unid.descricaoMotivo || '-' }}</td>
                  <td class="acoes-cell text-end pe-3">
                    <button type="button" class="btn-icon" @click="editarUnidade(unid, idx)"><i class="bi bi-pencil-fill"></i></button>
                    <button type="button" class="btn-icon text-danger" @click="item.unidadesConversao.splice(idx, 1)"><i class="bi bi-trash-fill"></i></button>
                  </td>
                </tr>
                <tr v-if="!item.unidadesConversao || item.unidadesConversao.length === 0"><td colspan="6" class="text-center py-4 text-muted">Nenhuma regra de conversão cadastrada.</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL DE UNIDADE -->
    <ModalFormulario v-model="modalUnidade" titulo="Regra de Conversão" @confirmar="salvarUnidadeLocal">
      <div v-if="unidadeEmEdicao" class="row g-3">
        <div class="col-md-12 mb-2">
           <div class="alert alert-info border-0 shadow-sm small py-2">
             Configure a regra de conversão entre unidades.
             <br>Exemplo: Se a Unidade Base é <strong>UN</strong>, e você fatura em Caixas com 12 unidades:
             <br><strong>[ 1 ] [ Caixa ] = [ 12 ] [ UN ]</strong>.
             <br><em>Nota: Conversões para aquisição (compra) são configuradas em Produto > Fornecedores.</em>
           </div>
        </div>

        <div class="col-md-5">
           <CampoInput id="qtdMedida" label="Qtd. na Unidade de Destino" type="number" min="0.001" step="any" v-model.number="unidadeEmEdicao.qtdUnidadeMedida" required />
        </div>
        <div class="col-md-7">
           <CampoBuscaLista
              id="lstUnidadeMedida"
              label="[Unidade de Destino]"
              v-model="unidadeEmEdicao.lstUnidadeMedida"
              tabela="ProdutoConversaoUnidades"
              @selecionado="onUnidadeSelecionada"
              required
            />
        </div>

        <div class="col-12 text-center py-2"><i class="bi bi-arrow-down-up fs-4 text-secondary"></i><br><span class="fw-bold">EQUIVALE A</span></div>

        <div class="col-md-5">
           <CampoInput id="qtdBase" label="Qtd. na Unidade Base" type="number" min="0.001" step="any" v-model.number="unidadeEmEdicao.qtdUnidadeBase" required />
        </div>
        <div class="col-md-7">
           <CampoInput id="unidBaseFake" label="[Unidade Base Atual]" :model-value="getTextoLista(item.lstUnidadeMedidaBase, listas.LstUnidadeMedidaBase || [])" disabled />
        </div>

        <div class="col-md-6 mt-4">
           <CampoSelect id="unidPadrao" label="Unidade Padrão de Conversão?" v-model="unidadeEmEdicao.unidadePadraoVenda" :options="simNaoOptions" option-value="id" option-label="label" />
        </div>
        <div class="col-md-6 mt-4">
           <CampoSelect id="unidSt" label="Status" v-model="unidadeEmEdicao.lstStatus" :options="listas.LstStatus || []" option-value="idListaClienteItem" option-label="textoLista" required />
        </div>
        <div class="col-md-12 mt-4">
          <CampoInput
            id="descricaoMotivo"
            label="Descrição/Motivo (opcional)"
            v-model="unidadeEmEdicao.descricaoMotivo"
            maxlength="150"
            placeholder="Ex: Para faturamento em caixa fechada"
          />
        </div>
      </div>
    </ModalFormulario>

    <!-- MODAL DE EXIGÊNCIA DE LPCO/LI -->
    <ModalFormulario v-model="modalExigencia" titulo="Exigência de LPCO/LI" @confirmar="salvarExigencia" :is-loading="isSavingExigencia">
      <div v-if="exigenciaEmEdicao" class="row g-3">
        <div class="col-md-12">
          <CampoBuscaLista
            id="LstOrgaoAnuente"
            label="Órgão Anuente"
            v-model="exigenciaEmEdicao.lstOrgaoAnuente"
            tabela="ProdutoExigenciasLicenca"
            required
          />
        </div>
        <div class="col-md-12">
          <CampoBuscaLista
            id="LstModeloLPCO"
            label="Modelo LPCO"
            v-model="exigenciaEmEdicao.lstModeloLPCO"
            tabela="ProdutoExigenciasLicenca"
            required
          />
        </div>
        <div class="col-md-12">
          <CampoBuscaLista
            id="LstTipoOperacaoComex"
            label="Operação Comex Exigida"
            v-model="exigenciaEmEdicao.lstTipoOperacaoComex"
            tabela="ProdutoExigenciasLicenca"
            required
          />
        </div>
        <div class="col-md-12">
          <CampoBuscaLista
            id="LstMomentoExigenciaLicenca"
            label="Momento da Exigência"
            v-model="exigenciaEmEdicao.lstMomentoExigenciaLicenca"
            tabela="ProdutoExigenciasLicenca"
            required
          />
        </div>
      </div>
    </ModalFormulario>

    <!-- MODAL DE NOME EM IDIOMA -->
    <ModalFormulario v-model="modalNomeIdioma" titulo="Nome em Outro Idioma" @confirmar="salvarNomeIdioma" :is-loading="isSavingNomeIdioma">
      <div v-if="nomeIdiomaEmEdicao" class="row g-3">
        <div class="col-md-12">
          <CampoBuscaLista
            id="lstIdioma"
            label="Idioma"
            v-model="nomeIdiomaEmEdicao.lstIdioma"
            tabela="ProdutoNomeIdioma"
            required
          />
        </div>
        <div class="col-md-12">
          <CampoInput
            id="nome"
            label="Nome do Produto no Idioma"
            v-model="nomeIdiomaEmEdicao.nome"
            required
            maxlength="200"
          />
        </div>
      </div>
    </ModalFormulario>

    <!-- Modal para Cadastro/Edição de Cláusula Comercial -->
    <ModalFormulario
      v-model="modalClausula"
      :titulo="clausulaEmEdicao?._isNew ? 'Nova Cláusula Comercial' : 'Editar Cláusula Comercial'"
      @confirmar="salvarClausula"
      :loading="isSavingClausula"
    >
      <div v-if="clausulaEmEdicao" class="row g-3">
        <div class="col-md-8">
          <CampoInput
            id="titulo"
            label="Título"
            v-model="clausulaEmEdicao.titulo"
            required
            maxlength="200"
          />
        </div>
        <div class="col-md-4">
          <CampoInput
            id="ordemExibicao"
            label="Ordem de Exibição"
            v-model="clausulaEmEdicao.ordemExibicao"
            type="decimal"
          />
        </div>
        <div class="col-12">
          <CampoInput
            id="texto"
            label="Texto da Cláusula"
            v-model="clausulaEmEdicao.texto"
            type="textarea"
            required
          />
        </div>
        <div class="col-md-4">
          <CampoSelect
            id="isDefinitiva"
            label="Cláusula Definitiva?"
            v-model="clausulaEmEdicao.isDefinitiva"
            :options="[
              { id: true, label: 'Sim' },
              { id: false, label: 'Não' }
            ]"
            option-value="id"
            option-label="label"
          />
        </div>
        <div class="col-md-4">
          <CampoSelect
            id="isFixa"
            label="Cláusula Fixa?"
            v-model="clausulaEmEdicao.isFixa"
            :options="[
              { id: true, label: 'Sim' },
              { id: false, label: 'Não' }
            ]"
            option-value="id"
            option-label="label"
          />
        </div>
        <div class="col-md-4">
          <CampoBuscaLista
            id="lstAplicacaoClausula"
            label="Aplicação (Módulo)"
            v-model="clausulaEmEdicao.lstAplicacaoClausula"
            required
            tabela="ProdutoClausulasComerciais"
            placeholder="Selecione a aplicação..."
          />
        </div>
        <div class="col-md-4">
          <CampoBuscaLista
            id="lstVisibilidade"
            label="Visibilidade"
            v-model="clausulaEmEdicao.lstVisibilidade"
            tabela="ProdutoClausulasComerciais"
            placeholder="Selecione a visibilidade..."
          />
        </div>
        <div class="col-md-4">
          <CampoSelect
            id="lstStatusClausula"
            label="Status"
            v-model="clausulaEmEdicao.lstStatusClausula"
            :options="listas.LstStatusClausula || []"
            option-value="idListaClienteItem"
            option-label="textoLista"
            placeholder="Selecione o status..."
          />
        </div>
      </div>
    </ModalFormulario>

    <!-- Modal para Cadastro/Edição de Cláusula Por Cliente -->
    <ModalFormulario
      v-model="modalClausulaCliente"
      :titulo="clausulaClienteEmEdicao?._isNew ? 'Nova Cláusula Por Cliente' : 'Editar Cláusula Por Cliente'"
      @confirmar="salvarClausulaCliente"
      :loading="isSavingClausulaCliente"
    >
      <div v-if="clausulaClienteEmEdicao" class="row g-3">
        <div class="col-md-8">
          <CampoInput
            id="titulo"
            label="Título"
            v-model="clausulaClienteEmEdicao.titulo"
            required
            maxlength="200"
          />
        </div>
        <div class="col-md-4">
          <CampoInput
            id="ordemExibicao"
            label="Ordem de Exibição"
            v-model="clausulaClienteEmEdicao.ordemExibicao"
            type="decimal"
          />
        </div>
        <div class="col-12">
          <CampoInput
            id="texto"
            label="Texto da Cláusula"
            v-model="clausulaClienteEmEdicao.texto"
            type="textarea"
            required
          />
        </div>
        <div class="col-md-4">
          <CampoBusca
            id="idParceiro"
            label="Cliente"
            v-model="clausulaClienteEmEdicao.idParceiro"
            :configuracao-busca="confBuscaCliente"
            required
          />
        </div>
        <div class="col-md-4">
          <CampoSelect
            id="isDefinitiva"
            label="Cláusula Definitiva?"
            v-model="clausulaClienteEmEdicao.isDefinitiva"
            :options="[
              { id: true, label: 'Sim' },
              { id: false, label: 'Não' }
            ]"
            option-value="id"
            option-label="label"
          />
        </div>
        <div class="col-md-4">
          <CampoBuscaLista
            id="lstAplicacaoClausula"
            label="Aplicação (Módulo)"
            v-model="clausulaClienteEmEdicao.lstAplicacaoClausula"
            tabela="ProdutoClienteClausula"
            placeholder="Selecione a aplicação..."
          />
        </div>
        <div class="col-md-4">
          <CampoBuscaLista
            id="lstVisibilidade"
            label="Visibilidade"
            v-model="clausulaClienteEmEdicao.lstVisibilidade"
            tabela="ProdutoClienteClausula"
            placeholder="Selecione a visibilidade..."
          />
        </div>
        <div class="col-md-4">
          <CampoSelect
            id="lstStatusClausula"
            label="Status"
            v-model="clausulaClienteEmEdicao.lstStatusClausula"
            :options="listas.LstStatusClausula || []"
            option-value="idListaClienteItem"
            option-label="textoLista"
            placeholder="Selecione o status..."
          />
        </div>
      </div>
    </ModalFormulario>

  </FormularioCadastro>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="Produto"
    :chave-primaria="item.idProduto"
    :tabelas-filhas="['ProdutoConversaoUnidades', 'ProdutoAtributo', 'ProdutoValorAtributoNcm', 'ProdutoExigenciasLicenca', 'ProdutoNomeIdioma', 'ProdutoClausulasComerciais', 'ProdutoClienteClausula']"
    chave-estrangeira="IdProduto"
    :titulo-registro="item.nome || 'Produto'"
    :dicionarios="{
      'LstStatus': listas.LstStatus || [],
      'idTipoProduto': tiposProdutoCache,
      'LstUnidadeMedidaBase': listas.LstUnidadeMedidaBase || [],
      'LstUnidadeMedidaVolume': listas.LstUnidadeMedidaVolume || [],
      'LstUnidadeMedida': listas.LstUnidadeMedida || [],
      'LstOrgaoAnuente': listas.LstOrgaoAnuente || [],
      'LstModeloLPCO': listas.LstModeloLPCO || [],
      'LstTipoOperacaoComex': listas.LstTipoOperacaoComex || [],
      'LstMomentoExigenciaLicenca': listas.LstMomentoExigenciaLicenca || [],
      'LstStatusClausula': listas.LstStatusClausula || [],
      'LstAplicacaoClausula': listas.LstAplicacaoClausula || [],
      'LstVisibilidade': listas.LstVisibilidade || []
    }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
:deep(.form-label) { font-weight: 400 !important; }

:deep(.form-label[required]:not([required="false"])::after),
:deep(.form-label[required="true"]::after) {
  content: " *"; color: #dc3545; font-weight: bold;
}

.pointer { cursor: pointer; }

.admin-table-container {
  background-color: transparent;
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.375rem;
  overflow-x: auto;
}
.admin-table thead th {
  background-color: var(--bs-tertiary-bg);
  color: var(--bs-heading-color);
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  border-bottom: 2px solid var(--bs-border-color-translucent);
}
.admin-table td {
  border-bottom: 1px solid var(--bs-border-color-translucent);
  color: var(--bs-body-color);
  vertical-align: middle;
}

:deep(.campo-input-container), :deep(.campo-select-container) { margin-bottom: 1rem; display: flex; flex-direction: column; }
.acoes-cell { text-align: right; vertical-align: middle; padding-right: 0.5rem; }
.btn-icon { background: none; border: none; color: #6c7d8e; padding: 0.4rem; transition: 0.2s; }
.btn-icon:hover { color: #212934; }
[data-bs-theme="dark"] .btn-icon:hover { color: #fff; }
</style>
